import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, AlertCircle } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter";
import { postJSON } from "../lib/api";
import { SEO } from "../components/SEO";

export const ResetPassword = () => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [err, setErr] = useState("");
    const [status, setStatus] = useState("idle");

    const submit = async (e) => {
        e.preventDefault();
        if (status === "loading") return;
        if (pw.length < 8 || !/[A-Za-z]/.test(pw) || !/\d/.test(pw)) {
            setErr("Password must be at least 8 chars and include a letter and a number.");
            return;
        }
        if (pw !== confirm) {
            setErr("Passwords don't match.");
            return;
        }
        setErr("");
        setStatus("loading");
        try {
            await postJSON("/auth/password/reset", { token, new_password: pw });
            navigate("/login?reset=success", { replace: true });
        } catch (e2) {
            setStatus("idle");
            setErr(e2.message || "Could not reset password.");
        }
    };

    return (
        <div className="pt-32 md:pt-40 pb-20 mx-auto max-w-md px-5" data-testid="reset-password-page">
            <SEO noindex title="Set new password" canonicalPath="/reset-password" />
            <Reveal>
                <div className="rounded-2xl border border-white/[0.06] bg-surface/70 backdrop-blur-md p-7">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-accent-cyan/10 border border-accent-cyan/20">
                        <KeyRound className="w-5 h-5 text-accent-cyan" />
                    </span>
                    <h1 className="mt-5 font-display font-semibold text-2xl tracking-tight" data-testid="reset-title">
                        Choose a new password
                    </h1>

                    {!token ? (
                        <p
                            className="mt-5 text-[13px] text-accent-red"
                            data-testid="reset-no-token"
                        >
                            Missing reset token. Request a new link from the{" "}
                            <Link to="/forgot-password" className="underline">
                                forgot-password
                            </Link>{" "}
                            page.
                        </p>
                    ) : (
                        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="reset-form">
                            <input
                                type="password"
                                required
                                placeholder="New password"
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                data-testid="reset-password"
                            />
                            <PasswordStrengthMeter password={pw} />
                            <input
                                type="password"
                                required
                                placeholder="Confirm new password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                data-testid="reset-confirm"
                            />
                            {err && (
                                <div
                                    className="flex items-center gap-2 text-[12px] text-accent-red bg-accent-red/5 border border-accent-red/20 rounded-md px-3 py-2"
                                    data-testid="reset-error"
                                >
                                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                    {err}
                                </div>
                            )}
                            <MagneticButton
                                type="submit"
                                disabled={status === "loading"}
                                className="w-full py-2.5 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-40 transition-shadow duration-200"
                                data-testid="reset-submit"
                                ariaLabel="Update password"
                            >
                                {status === "loading" ? "Updating…" : "Update password"}
                            </MagneticButton>
                        </form>
                    )}
                </div>
            </Reveal>
        </div>
    );
};

export default ResetPassword;
